import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ShoppingCart, User, LogOut, Shield, Clock, Gavel, Tag, 
  ArrowLeft, Plus, Minus, AlertCircle, Home, Bell, BellOff, Zap,
  ChevronLeft, ChevronRight, X, DollarSign, Calendar, Share2, Copy, Check, ArrowRightLeft
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import MakeOfferModal from '../components/MakeOfferModal';
import TradeOfferModal from '../components/TradeOfferModal';

const API = `${'https://shopblkmrkt.com'}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";
const DEFAULT_PRODUCT_IMAGE = "https://static.prod-images.emergentagent.com/jobs/d742bfc9-e565-49d9-b972-9ec11d4b1acf/images/8bffc3d80961a69d81ff6049f0b0e3b52871da1a055b9513c572b6a653fd3377.png";

// Helper to resolve image URLs - handles both relative and absolute URLs
const resolveImageUrl = (url) => {
  if (!url || url.trim() === '') return DEFAULT_PRODUCT_IMAGE;
  
  // If it's a broken old preview URL, return default immediately
  if (url.includes('preview.emergentagent.com') && !url.includes('rare-deck-hub')) {
    console.warn('Broken preview URL detected:', url);
    return DEFAULT_PRODUCT_IMAGE;
  }
  
  // If already absolute URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  
  // If relative URL, prepend backend URL
  const backendBase = 'https://shopblkmrkt.com';
  return `${backendBase}${url}`;
};

// Append ?size=thumb|medium to /api/images/* URLs so the browser downloads a
// ~10–100KB resized JPG instead of the multi-MB original. Critical: rendering
// 5+ full-res iPhone photos in 64px thumbnails allocates GB of RGBA framebuffer
// and OOM-crashes the browser tab.
const sizedImageUrl = (url, size = 'medium') => {
  const resolved = resolveImageUrl(url);
  if (!resolved || typeof resolved !== 'string') return resolved;
  if (!resolved.includes('/api/images/')) return resolved;
  return resolved + (resolved.includes('?') ? '&' : '?') + 'size=' + size;
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userProfile, logout, getAuthHeader, loading: authLoading, authResolved, hasToken, hasValidToken } = useAuth();
  const { cart, itemCount, addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  // Initialize hasAccess from sessionStorage for instant display on refresh
  const [hasAccess, setHasAccess] = useState(() => {
    return sessionStorage.getItem('blkmrkt_access') === 'granted';
  });
  const [fbconMode, setFbconMode] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [bidAmount, setBidAmount] = useState('');
  const [bidding, setBidding] = useState(false);
  const [startingPlan, setStartingPlan] = useState(null); // 'flex_pay' or 'standard' or null
  
  // Ref for auto-scrolling to action buttons
  const actionRef = useRef(null);
  
  // Image gallery state
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [bidError, setBidError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  // When user clicks MAKE OFFER or OFFER TRADE without picking a variant,
  // show an inline picker then auto-open the chosen modal.
  const [variantPicker, setVariantPicker] = useState({ open: false, next: null });
  
  // Inline notification state (shows below buttons)
  const [inlineNotification, setInlineNotification] = useState(null);
  
  // Max Bid (Auto-Bid) state
  const [maxBidAmount, setMaxBidAmount] = useState('');
  const [myMaxBid, setMyMaxBid] = useState(null);
  const [settingMaxBid, setSettingMaxBid] = useState(false);
  const [showMaxBidInput, setShowMaxBidInput] = useState(false);
  const [wasOutbid, setWasOutbid] = useState(false);
  const [outbidAmount, setOutbidAmount] = useState(null);
  
  // Restock Notification state
  const [restockSubscribed, setRestockSubscribed] = useState(false);
  const [subscribingRestock, setSubscribingRestock] = useState(false);
  
  // Gift card state
  const [giftCardAmount, setGiftCardAmount] = useState('50');
  const [giftCardCustomAmount, setGiftCardCustomAmount] = useState('');
  const [giftCardRecipientEmail, setGiftCardRecipientEmail] = useState('');
  const [giftCardRecipientName, setGiftCardRecipientName] = useState('');
  const [giftCardSenderName, setGiftCardSenderName] = useState('');
  const [giftCardMessage, setGiftCardMessage] = useState('');
  
  // BLKCRD membership state
  const [blkcrdTier, setBlkcrdTier] = useState('1_month');
  const [blkcrdAutoRenew, setBlkcrdAutoRenew] = useState(false);
  const BLKCRD_TIERS = {
    '1_month': { months: 1, price: 50, name: '1 MONTH', savings: 0 },
    '3_month': { months: 3, price: 125, name: '3 MONTHS', savings: 25 },
    '6_month': { months: 6, price: 225, name: '6 MONTHS', savings: 75 },
  };
  
  // Offer modal state
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [userOffer, setUserOffer] = useState(null);
  
  // Trade offer modal state
  const [showTradeOfferModal, setShowTradeOfferModal] = useState(false);
  
  // Share link state
  const [shareCopied, setShareCopied] = useState(false);
  
  // Payment plan dialog state
  const [paymentPlanDialog, setPaymentPlanDialog] = useState({ open: false, planType: null, priorityShipping: false, overnightShipping: false });
  
  // Related products state
  const [relatedProducts, setRelatedProducts] = useState([]);
  
  // Generate share URL using the API endpoint that handles OG tags
  const getShareUrl = () => {
    // Use production URL for sharing (preview URLs won't work on social media)
    return `https://shopblkmrkt.com/api/p/${id}`;
  };
  
  // Copy share link to clipboard
  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setShareCopied(true);
      toast.success('Share link copied! Paste on social media for link previews.');
      setTimeout(() => setShareCopied(false), 3000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  // Brand colors map
  const [brandColors, setBrandColors] = useState({});

  // Fetch brand colors
  useEffect(() => {
    const fetchBrandColors = async () => {
      try {
        const response = await axios.get(`${API}/brands/map`);
        setBrandColors(response.data || {});
      } catch (error) {
        // Silently fail
      }
    };
    fetchBrandColors();
  }, []);

  // Check access on mount - REQUIRE LOGIN
  // CRITICAL: Wait for authResolved before making any navigation decisions
  useEffect(() => {
    const checkAccess = async () => {
      // WAIT for auth to be resolved before checking anything
      if (!authResolved) {
        return;
      }
      
      // Check for token - this is the source of truth
      const tokenExists = hasValidToken();
      
      // No token = definitely need to login
      if (!tokenExists) {
        navigate('/login');
        return;
      }
      
      // IMMEDIATELY grant access if user had it before (handles rapid refresh)
      const hadPreviousAccess = sessionStorage.getItem('blkmrkt_access') === 'granted';
      if (hadPreviousAccess) {
        setHasAccess(true);
        // Still verify in background, but don't block
      }
      
      // Security: Check current drop status from backend
      try {
        const response = await axios.get(`${API}/drop/status`, { timeout: 5000 });
        
        // Store fbcon mode
        if (response.data.fbcon_mode) {
          setFbconMode(true);
        }
        
        if (response.data.is_open) {
          setHasAccess(true);
          sessionStorage.setItem('blkmrkt_access', 'granted');
          return;
        }
        
        if (response.data.access_code) {
          const storedAccess = sessionStorage.getItem('blkmrkt_access');
          const storedCode = sessionStorage.getItem('blkmrkt_access_code');
          const hasTempAccess = sessionStorage.getItem('blkmrkt_temp_access') === 'true';
          
          if (storedAccess === 'granted' && (hasTempAccess || storedCode === response.data.access_code)) {
            setHasAccess(true);
            return;
          }
        }
        
        // Drop is closed - only redirect if didn't have previous access
        if (!hadPreviousAccess) {
          sessionStorage.removeItem('blkmrkt_access');
          sessionStorage.removeItem('blkmrkt_access_code');
          navigate('/');
        }
      } catch (error) {
        console.error('Failed to check drop status:', error);
        // On network error, TRUST previous access - don't redirect
        if (!hadPreviousAccess && error.response) {
          navigate('/');
        }
      }
    };
    
    checkAccess();
  }, [navigate, authResolved, hasValidToken]);

  const fetchProduct = useCallback(async () => {
    let retries = 2;
    
    const attemptFetch = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`, { 
          timeout: 8000,
          headers: getAuthHeader()
        });
        setProduct(response.data);
        if (response.data.is_bidding) {
          const minBid = response.data.current_bid 
            ? response.data.current_bid + 5
            : response.data.price;
          setBidAmount(minBid.toFixed(2));
        }
        return true;
      } catch (error) {
        // Only treat as "not found" if server explicitly says 404
        if (error.response?.status === 404) {
          toast.error('Product not found');
          navigate('/catalog');
          return true; // Don't retry
        }
        return false; // Retry on network errors
      }
    };
    
    let success = await attemptFetch();
    while (!success && retries > 0) {
      retries--;
      await new Promise(r => setTimeout(r, 500));
      success = await attemptFetch();
    }
    
    if (!success) {
      // Network error after retries - show error but don't redirect
      console.error('Failed to fetch product after retries');
      toast.error('Network error - please refresh');
    }
    
    setLoading(false);
  }, [id, navigate]);

  const fetchBids = useCallback(async () => {
    if (!product?.is_bidding) return;
    try {
      // If admin, fetch with full user info
      if (user?.is_admin) {
        const response = await axios.get(`${API}/admin/products/${id}/bids`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('blkmrkt_token')}` }
        });
        setBids(response.data.bids || []);
      } else {
        const response = await axios.get(`${API}/products/${id}/bids`);
        setBids(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch bids:', error);
      // Fallback to public endpoint
      try {
        const response = await axios.get(`${API}/products/${id}/bids`);
        setBids(response.data);
      } catch (e) {}
    }
  }, [id, product?.is_bidding, user?.is_admin]);

  useEffect(() => {
    if (hasAccess) {
      fetchProduct();
    }
  }, [fetchProduct, hasAccess]);

  // Scroll to top when product ID changes (e.g., clicking related product)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  // Auto-scroll to action area after product loads
  useEffect(() => {
    if (product && !loading && actionRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        actionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [product, loading]);

  // Bid polling - optimized to reduce server load
  useEffect(() => {
    if (!hasAccess || !product?.is_bidding) return;
    
    fetchBids();
    
    // Fixed 5-second polling - more frequent polling causes server overload
    const interval = setInterval(fetchBids, 5000);
    return () => clearInterval(interval);
  }, [fetchBids, hasAccess, product?.is_bidding]);

  // Fetch related products (same brand, then similar price)
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!product) return;
      
      try {
        // Fetch all products
        const response = await axios.get(`${API}/products`, {
          params: { limit: 100 }
        });
        
        const allProducts = response.data.products || response.data || [];
        
        // Filter out current product and out of stock items
        const available = allProducts.filter(p => 
          p.id !== product.id && 
          p.stock > 0
        );
        
        // Priority 1: Same brand
        const sameBrand = available.filter(p => 
          p.brand && product.brand && 
          p.brand.toUpperCase() === product.brand.toUpperCase()
        );
        
        // Start with same brand products
        const combined = [...sameBrand].slice(0, 4);
        
        // If not enough same brand, fill with similar price range
        if (combined.length < 4) {
          const currentPrice = product.price || 0;
          const priceRange = currentPrice * 0.3; // 30% price range
          
          // Get products in similar price range (not already included)
          const similarPrice = available
            .filter(p => 
              !combined.find(c => c.id === p.id) &&
              p.price >= currentPrice - priceRange &&
              p.price <= currentPrice + priceRange
            )
            .sort((a, b) => Math.abs(a.price - currentPrice) - Math.abs(b.price - currentPrice));
          
          combined.push(...similarPrice.slice(0, 4 - combined.length));
        }
        
        // If still not enough, add any other products
        if (combined.length < 4) {
          const others = available
            .filter(p => !combined.find(c => c.id === p.id))
            .sort(() => Math.random() - 0.5);
          
          combined.push(...others.slice(0, 4 - combined.length));
        }
        
        setRelatedProducts(combined);
      } catch (error) {
        console.error('Failed to fetch related products:', error);
      }
    };
    
    fetchRelatedProducts();
  }, [product]);

  // Fetch user's max bid for auction items
  useEffect(() => {
    const fetchMyMaxBid = async () => {
      if (!hasAccess || !product?.is_bidding || !user) return;
      try {
        const response = await axios.get(`${API}/products/${id}/max-bid`, {
          headers: getAuthHeader()
        });
        setMyMaxBid(response.data);
        if (response.data) {
          setMaxBidAmount(response.data.max_amount.toFixed(2));
        }
      } catch (error) {
        // User may not have a max bid set
      }
    };
    fetchMyMaxBid();
  }, [hasAccess, product?.is_bidding, id, user, getAuthHeader]);

  // Fetch restock notification status for out-of-stock items
  useEffect(() => {
    const fetchRestockStatus = async () => {
      if (!hasAccess || !product || product.stock > 0 || product.is_bidding || !user) return;
      try {
        const response = await axios.get(`${API}/products/${id}/notify-restock/status`, {
          headers: getAuthHeader()
        });
        setRestockSubscribed(response.data.subscribed);
      } catch (error) {
        // Ignore errors
      }
    };
    fetchRestockStatus();
  }, [hasAccess, product, id, user, getAuthHeader]);

  // Keep selectedVariant in sync with product data
  // This handles cases where product data updates (e.g., after a sale) 
  // and the selectedVariant needs to reflect the latest data
  useEffect(() => {
    if (!selectedVariant || !product?.variants) return;
    
    // Find the current variant in the updated product data
    const updatedVariant = product.variants.find(v => v.id === selectedVariant.id);
    
    if (!updatedVariant) {
      // Variant no longer exists - clear selection
      setSelectedVariant(null);
      setSelectedImageIndex(0);
    } else if (
      updatedVariant.stock !== selectedVariant.stock ||
      updatedVariant.name !== selectedVariant.name ||
      JSON.stringify(updatedVariant.image_indices) !== JSON.stringify(selectedVariant.image_indices)
    ) {
      // Variant data changed - update the selection
      setSelectedVariant(updatedVariant);
      
      // If the variant is now out of stock, try to select the first in-stock variant
      if (updatedVariant.stock <= 0) {
        const firstInStock = product.variants.find(v => v.stock > 0);
        if (firstInStock) {
          setSelectedVariant(firstInStock);
          setSelectedImageIndex(0);
        }
      }
    }
  }, [product?.variants, selectedVariant]);

  // Fetch user's existing offer on this product
  useEffect(() => {
    const fetchUserOffer = async () => {
      if (!hasAccess || !product || !product.allow_offers || !user) return;
      try {
        const response = await axios.get(`${API}/products/${id}/my-offer`, {
          headers: getAuthHeader()
        });
        setUserOffer(response.data);
      } catch (error) {
        // No offer exists or error - that's fine
        setUserOffer(null);
      }
    };
    fetchUserOffer();
  }, [hasAccess, product, id, user, getAuthHeader]);

  // Poll for auction status updates (real-time feel)
  useEffect(() => {
    if (!hasAccess || !product?.is_bidding) return;
    
    const pollAuctionStatus = async () => {
      try {
        const response = await axios.get(`${API}/auctions/status`);
        const { auctions } = response.data;
        const auctionData = auctions?.find(a => a.id === id);
        
        if (auctionData && product) {
          // Check if bid changed
          if (auctionData.current_bid !== product.current_bid) {
            const previousBidderId = product.current_bidder_id;
            const newBidderId = auctionData.current_bidder_id;
            
            // Check if user WAS the highest bidder but is no longer
            if (user && previousBidderId === user.id && newBidderId !== user.id) {
              // User has been outbid!
              setWasOutbid(true);
              setOutbidAmount(auctionData.current_bid);
              
              // Set the bid input to the next minimum bid
              const nextMinBid = (auctionData.current_bid || 0) + 5;
              setBidAmount(nextMinBid.toFixed(2));
            }
            
            setProduct(prev => ({
              ...prev,
              current_bid: auctionData.current_bid,
              current_bidder_id: auctionData.current_bidder_id
            }));
            // Refresh bids list
            fetchBids();
          }
          
          // Check if auction ended
          if (auctionData.is_ended && timeRemaining !== 'ENDED') {
            setTimeRemaining('ENDED');
            fetchProduct(); // Refresh full product to get final state
          }
        }
      } catch (error) {
        // Silently fail
      }
    };
    
    const interval = setInterval(pollAuctionStatus, 10000); // Every 10 seconds
    return () => clearInterval(interval);
  }, [hasAccess, product, id, timeRemaining, fetchBids, fetchProduct]);

  useEffect(() => {
    if (!product?.bid_end_time) return;
    
    const updateTimer = () => {
      const end = new Date(product.bid_end_time);
      const now = new Date();
      const diff = end - now;
      
      if (diff <= 0) {
        setTimeRemaining('ENDED');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeRemaining(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [product?.bid_end_time]);

  // Format auction end time for display (e.g., "ENDS 9:00 PM EST")
  const formatAuctionEndTime = (endTime) => {
    if (!endTime) return null;
    const end = new Date(endTime);
    const options = { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true,
      timeZone: 'America/New_York'
    };
    const timeStr = end.toLocaleTimeString('en-US', options).toUpperCase();
    return `ENDS ${timeStr} EST`;
  };

  // Handle starting a payment plan
  const handleStartPaymentPlan = async (planType) => {
    if (!user) {
      toast.error('Please login to start a payment plan');
      navigate('/login');
      return;
    }
    
    // Open the styled confirmation dialog with shipping upgrades off by default
    setPaymentPlanDialog({ open: true, planType, priorityShipping: false, overnightShipping: false });
  };
  
  // Actually start the payment plan after confirmation
  const confirmStartPaymentPlan = async () => {
    const planType = paymentPlanDialog.planType;
    const overnightShipping = paymentPlanDialog.overnightShipping || false;
    const priorityShipping = (paymentPlanDialog.priorityShipping && !overnightShipping) || false;
    setPaymentPlanDialog({ open: false, planType: null, priorityShipping: false, overnightShipping: false });
    
    setStartingPlan(planType);
    try {
      const response = await axios.post(
        `${API}/products/${product.id}/payment-plan`,
        { 
          product_id: product.id, 
          plan_type: planType,
          variant_id: selectedVariant?.id || null,
          priority_shipping: priorityShipping,
          overnight_shipping: overnightShipping
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Payment plan started! Check your dashboard for payment schedule.`);
      navigate('/dashboard');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start payment plan';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start payment plan');
    } finally {
      setStartingPlan(null);
    }
  };
  
  // Calculate payment plan details for display
  const getPaymentPlanDetails = (planType, priorityShipping = false, overnightShipping = false) => {
    if (!product) return null;
    const effectivePrice = selectedVariant?.price != null ? selectedVariant.price : product.price;
    const shippingCost = overnightShipping ? 50.00 : (priorityShipping ? 15.00 : 0.00);
    const arrangementFee = effectivePrice * 0.20;
    const total = effectivePrice + arrangementFee + shippingCost;
    
    if (planType === 'standard') {
      // STANDARD: 50% down, 50% in 7 days (2 payments)
      const downPayment = total * 0.50;
      return {
        title: 'STANDARD PLAN',
        downPayment,
        basePrice: effectivePrice,
        arrangementFee,
        shippingCost,
        total,
        schedule: [
          { label: 'DOWN PAYMENT (50%)', amount: downPayment, due: 'DUE NOW' },
          { label: 'FINAL PAYMENT (50%)', amount: total - downPayment, due: 'DUE IN 7 DAYS' }
        ]
      };
    } else {
      // FLEX PAY: 33% down, 33% on day 7, 33% on day 14 (3 payments)
      const paymentAmount = total / 3;
      return {
        title: 'FLEX PAY',
        downPayment: paymentAmount,
        basePrice: effectivePrice,
        arrangementFee,
        shippingCost,
        total,
        schedule: [
          { label: 'DOWN PAYMENT (33%)', amount: paymentAmount, due: 'DUE NOW' },
          { label: 'PAYMENT 2 (33%)', amount: paymentAmount, due: 'DUE IN 7 DAYS' },
          { label: 'PAYMENT 3 (33%)', amount: paymentAmount, due: 'DUE IN 14 DAYS' }
        ]
      };
    }
  };

  // Show inline notification below buttons
  const showInlineNotification = (message, type = 'success') => {
    setInlineNotification({ message, type });
    setTimeout(() => setInlineNotification(null), 1500);
  };

  const handleAddToCart = async () => {
    if (!user) {
      showInlineNotification('PLEASE LOGIN', 'error');
      navigate('/login');
      return;
    }
    
    // Handle BLKCRD membership products
    if (product.is_blkcrd) {
      const tierInfo = BLKCRD_TIERS[blkcrdTier];
      if (!tierInfo) {
        showInlineNotification('SELECT A TIER', 'error');
        return;
      }
      
      const blkcrdDetails = {
        tier: blkcrdTier,
        auto_renew: blkcrdAutoRenew
      };
      
      const success = await addToCart(product.id, 1, false, false, null, null, blkcrdDetails);
      if (success) {
        showInlineNotification('BLKCRD ADDED', 'success');
      }
      return;
    }
    
    // Handle gift card products
    if (product.is_gift_card) {
      const amount = giftCardAmount === 'custom' 
        ? parseFloat(giftCardCustomAmount) 
        : parseFloat(giftCardAmount);
      
      if (!amount || amount <= 0) {
        showInlineNotification('ENTER A VALID AMOUNT', 'error');
        return;
      }
      if (amount > 500) {
        showInlineNotification('MAX AMOUNT IS $500', 'error');
        return;
      }
      if (!giftCardRecipientEmail || !giftCardRecipientEmail.includes('@')) {
        showInlineNotification('ENTER RECIPIENT EMAIL', 'error');
        return;
      }
      
      const giftCardDetails = {
        amount,
        recipient_email: giftCardRecipientEmail,
        recipient_name: giftCardRecipientName || null,
        sender_name: giftCardSenderName || user.name || null,
        message: giftCardMessage || null
      };
      
      const success = await addToCart(product.id, 1, false, false, null, giftCardDetails);
      if (success) {
        showInlineNotification('GIFT CARD ADDED', 'success');
        // Reset gift card fields
        setGiftCardRecipientEmail('');
        setGiftCardRecipientName('');
        setGiftCardMessage('');
      }
      return;
    }
    
    // Check if variant selection is required
    if (product.has_variants && product.variants?.length > 0 && !selectedVariant) {
      showInlineNotification('SELECT AN OPTION', 'error');
      return;
    }
    const success = await addToCart(product.id, quantity, false, false, selectedVariant?.id);
    if (success) {
      showInlineNotification('ADDED TO CART', 'success');
    }
  };

  const handleProceedToCheckout = async () => {
    if (!user) {
      // Store the product ID and redirect intent
      sessionStorage.setItem('blkmrkt_checkout_product', product.id);
      sessionStorage.setItem('blkmrkt_checkout_qty', quantity.toString());
      toast.error('Please login to purchase');
      navigate('/login?redirect=checkout');
      return;
    }
    
    // Handle BLKCRD membership products
    if (product.is_blkcrd) {
      const tierInfo = BLKCRD_TIERS[blkcrdTier];
      if (!tierInfo) {
        toast.error('Please select a tier');
        return;
      }
      
      const blkcrdDetails = {
        tier: blkcrdTier,
        auto_renew: blkcrdAutoRenew
      };
      
      const success = await addToCart(product.id, 1, false, false, null, null, blkcrdDetails);
      if (success) {
        navigate('/checkout');
      }
      return;
    }
    
    // Handle gift card products
    if (product.is_gift_card) {
      const amount = giftCardAmount === 'custom' 
        ? parseFloat(giftCardCustomAmount) 
        : parseFloat(giftCardAmount);
      
      if (!amount || amount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      if (amount > 500) {
        toast.error('Maximum gift card amount is $500');
        return;
      }
      if (!giftCardRecipientEmail || !giftCardRecipientEmail.includes('@')) {
        toast.error('Please enter recipient email');
        return;
      }
      
      const giftCardDetails = {
        amount,
        recipient_email: giftCardRecipientEmail,
        recipient_name: giftCardRecipientName || null,
        sender_name: giftCardSenderName || user.name || null,
        message: giftCardMessage || null
      };
      
      const success = await addToCart(product.id, 1, false, false, null, giftCardDetails);
      if (success) {
        navigate('/checkout');
      }
      return;
    }
    
    // Check if variant selection is required
    if (product.has_variants && product.variants?.length > 0 && !selectedVariant) {
      toast.error('Please select an option');
      return;
    }
    
    // Check if this product (with this variant if applicable) is already in the cart
    const isInCart = cart.items.some(item => {
      if (selectedVariant) {
        return item.product_id === product.id && item.variant_id === selectedVariant.id;
      }
      return item.product_id === product.id;
    });
    
    if (isInCart) {
      // Item already in cart - just go to checkout
      navigate('/checkout');
    } else {
      // Item not in cart - add it first, then go to checkout
      const success = await addToCart(product.id, quantity, false, false, selectedVariant?.id);
      if (success) {
        navigate('/checkout');
      }
    }
  };

  const handleBuyNow = async () => {
    if (!user) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }
    // Pass isBuyNow=true for bidding items with Buy It Now option
    const success = await addToCart(product.id, 1, false, true);
    if (success) {
      navigate('/checkout');
    }
  };

  const handlePlaceBid = async () => {
    if (!user) {
      toast.error('Please login to bid');
      navigate('/login');
      return;
    }
    
    // Check if user is already highest bidder (frontend validation)
    if (bids.length > 0 && bids[0].user_id === user.id) {
      toast.error('You are already the highest bidder!');
      return;
    }
    
    // Clear any previous error
    setBidError('');
    
    try {
      setBidding(true);
      await axios.post(
        `${API}/products/${id}/bids`,
        { product_id: id, amount: parseFloat(bidAmount) },
        { headers: getAuthHeader() }
      );
      toast.success('BID PLACED');
      setWasOutbid(false); // Clear outbid status when user places new bid
      
      // Fetch updated product to check if we're still highest bidder
      const updatedProduct = await axios.get(`${API}/products/${id}`);
      const newProduct = updatedProduct.data;
      
      // Check if auto-bid immediately outbid us
      if (newProduct.current_bidder_id && newProduct.current_bidder_id !== user?.id) {
        // We were immediately outbid by auto-bid!
        setWasOutbid(true);
        setOutbidAmount(newProduct.current_bid);
        
        // Set the bid input to the next minimum bid
        const nextMinBid = (newProduct.current_bid || 0) + 5;
        setBidAmount(nextMinBid.toFixed(2));
      }
      
      setProduct(newProduct);
      await fetchBids();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to place bid';
      setBidError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setBidding(false);
    }
  };

  // Handle setting max bid (auto-bid)
  const handleSetMaxBid = async () => {
    if (!user) {
      toast.error('Please login to set max bid');
      navigate('/login');
      return;
    }
    
    const amount = parseFloat(maxBidAmount);
    if (isNaN(amount) || amount < minBid + 5) {
      toast.error(`Max bid must be at least $${(minBid + 5).toFixed(2)}`);
      return;
    }
    
    try {
      setSettingMaxBid(true);
      const response = await axios.post(
        `${API}/products/${id}/max-bid`,
        { max_amount: amount },
        { headers: getAuthHeader() }
      );
      setMyMaxBid(response.data);
      setShowMaxBidInput(false);
      setWasOutbid(false); // Clear outbid status when user sets new max bid
      toast.success(`Auto-bid set to $${amount.toFixed(2)}`);
      await fetchProduct();
      await fetchBids();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to set max bid';
      toast.error(errorMsg);
    } finally {
      setSettingMaxBid(false);
    }
  };

  // Handle canceling max bid
  const handleCancelMaxBid = async () => {
    try {
      setSettingMaxBid(true);
      await axios.delete(`${API}/products/${id}/max-bid`, {
        headers: getAuthHeader()
      });
      setMyMaxBid(null);
      setMaxBidAmount('');
      toast.success('Auto-bid cancelled');
    } catch (error) {
      toast.error('Failed to cancel auto-bid');
    } finally {
      setSettingMaxBid(false);
    }
  };

  // Handle subscribing to restock notifications
  const handleRestockSubscribe = async () => {
    if (!user) {
      toast.error('Please login to get notified');
      navigate('/login');
      return;
    }
    
    try {
      setSubscribingRestock(true);
      await axios.post(
        `${API}/products/${id}/notify-restock`,
        {},
        { headers: getAuthHeader() }
      );
      setRestockSubscribed(true);
      toast.success('You will be notified when back in stock!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribingRestock(false);
    }
  };

  // Handle unsubscribing from restock notifications
  const handleRestockUnsubscribe = async () => {
    try {
      setSubscribingRestock(true);
      await axios.delete(`${API}/products/${id}/notify-restock`, {
        headers: getAuthHeader()
      });
      setRestockSubscribed(false);
      toast.success('Notification cancelled');
    } catch (error) {
      toast.error('Failed to unsubscribe');
    } finally {
      setSubscribingRestock(false);
    }
  };

  // Check if current user is the highest bidder
  const isHighestBidder = user && bids.length > 0 && bids[0].user_id === user.id;

  // Get the available images based on whether a variant is selected
  // If variant has image_indices (array), show only those images
  // If variant has image_index (single), use legacy behavior
  const getVariantImages = useCallback(() => {
    if (!selectedVariant || !product?.images) return null;
    
    // New: Support for multiple images per variant (image_indices array)
    if (selectedVariant.image_indices && Array.isArray(selectedVariant.image_indices) && selectedVariant.image_indices.length > 0) {
      return selectedVariant.image_indices
        .filter(idx => idx >= 0 && idx < product.images.length)
        .map(idx => ({ originalIndex: idx, url: product.images[idx] }));
    }
    
    // Legacy: Single image_index
    if (selectedVariant.image_index !== undefined && selectedVariant.image_index !== null) {
      const idx = selectedVariant.image_index;
      if (idx >= 0 && idx < product.images.length) {
        return [{ originalIndex: idx, url: product.images[idx] }];
      }
    }
    
    return null; // No specific images for this variant
  }, [selectedVariant, product?.images]);

  const variantImages = useMemo(() => getVariantImages(), [getVariantImages]);
  const displayImages = useMemo(() => {
    return variantImages ? variantImages : (product?.images?.map((url, idx) => ({ originalIndex: idx, url })) || []);
  }, [variantImages, product?.images]);

  // Reset selectedImageIndex when displayImages changes to prevent out-of-bounds
  useEffect(() => {
    if (displayImages.length > 0 && selectedImageIndex >= displayImages.length) {
      setSelectedImageIndex(0);
    }
  }, [displayImages.length, selectedImageIndex]);

  const getProductImage = (size) => {
    const sizeOrPass = (u) => size ? sizedImageUrl(u, size) : resolveImageUrl(u);
    // Use the currently selected image from the display images (supports multi-image variants)
    if (displayImages.length > 0 && selectedImageIndex < displayImages.length) {
      return sizeOrPass(displayImages[selectedImageIndex].url);
    }
    
    // Fallback: If a variant is selected and has its own image_url (legacy single image), use that
    if (selectedVariant?.image_url) {
      return sizeOrPass(selectedVariant.image_url);
    }
    
    // Fallback to product's main image
    if (product?.images && product.images.length > 0) {
      return sizeOrPass(product.images[0]);
    }
    return sizeOrPass(product?.image_url);
  };

  const minBid = product?.current_bid ? product.current_bid + 5 : product?.price || 0;
  const maxBid = product?.buy_now_price ? product.buy_now_price - 0.01 : null;
  const reserveMet = product?.reserve_price ? (product?.current_bid || 0) >= product.reserve_price : true;
  
  // Check if gap to BIN is less than $5 - must use Buy It Now
  const currentHighest = product?.current_bid || product?.price || 0;
  const gapToBin = product?.buy_now_price ? product.buy_now_price - currentHighest : null;
  const mustUseBuyNow = gapToBin !== null && gapToBin < 5;

  // Helper to check if product is an ended auction that wasn't sold (includes buy_now_sold as sold)
  // Failed auctions (reserve_not_met, no_bids) remain UNAVAILABLE until re-auctioned
  const isEndedAuction = product?.auction_processed && product?.auction_result && 
    product?.auction_result !== 'sold' && product?.auction_result !== 'buy_now_sold';
  
  // Helper to check if this is a sold auction (either processed or stock=0 with auction markers)
  const isSoldAuction = (product?.stock === 0 && (product?.is_bidding || product?.bid_end_time)) ||
    (product?.auction_processed && (product?.auction_result === 'sold' || product?.auction_result === 'buy_now_sold'));

  // Helper to get auction end status text
  const getAuctionEndStatus = () => {
    // If stock is 0 and was an auction (has bid_end_time or is_bidding), show as SOLD OUT
    if (product?.stock === 0 && (product?.is_bidding || product?.bid_end_time) && !isEndedAuction) {
      return 'SOLD OUT';
    }
    if (!product?.auction_processed) return null;
    
    switch (product.auction_result) {
      case 'no_bids':
        return 'RESERVE NOT MET';  // Always show RESERVE NOT MET, never NO BIDS
      case 'reserve_not_met':
        return 'RESERVE NOT MET';
      case 'sold':
        return 'SOLD OUT';
      case 'buy_now_sold':
        return 'SOLD OUT';
      default:
        return 'RESERVE NOT MET';
    }
  };

  // Helper to check if product should show as unavailable
  // Failed auctions remain UNAVAILABLE (even with stock > 0) until admin re-auctions them
  const isUnavailable = product?.stock === 0 || isEndedAuction || isSoldAuction;

  // Don't render until we have access
  // If user had previous access (from sessionStorage), show immediately
  const showLoading = !hasAccess && (!authResolved || authLoading);
  
  if (showLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono">VERIFYING ACCESS...</div>
      </div>
    );
  }
  
  // If auth resolved but no access, the useEffect will handle redirect
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono">VERIFYING ACCESS...</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono cursor-blink">LOADING</div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  // Get the best image for OG tags
  const ogImage = product.images?.[0] || product.image_url || DEFAULT_PRODUCT_IMAGE;
  const ogImageUrl = ogImage?.startsWith('http') ? ogImage : `https://shopblkmrkt.com${ogImage}`;
  const ogDescription = product.description?.substring(0, 200) || 'Exclusive Fingerboard Equipment';
  const ogPrice = product.price ? `$${product.price.toFixed(2)} - ` : '';

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Note: Dynamic Helmet meta tags temporarily disabled due to react-helmet-async compatibility issue.
          Use the share button to copy the social-media-friendly link instead. */}
      
      {/* Header */}
      <header className="fixed left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm" style={{ top: 'var(--banner-offset, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-4">
            {(user || hasToken) ? (
              <>
                <Link 
                  to="/catalog"
                  className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                  data-testid="home-link"
                >
                  <Home className="w-5 h-5" />
                </Link>
                <Link 
                  to="/cart" 
                  className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                  data-testid="cart-link"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                      {itemCount}
                    </span>
                  )}
                </Link>
                <Link 
                  to="/dashboard" 
                  className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                  data-testid="dashboard-link"
                >
                  <User className="w-5 h-5" />
                </Link>
                {user?.is_admin && (
                  <Link 
                    to="/admin" 
                    className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                    data-testid="admin-link"
                  >
                    <Shield className="w-5 h-5" />
                  </Link>
                )}
                <button 
                  onClick={logout}
                  className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <Link 
                to="/login"
                className="text-sm font-mono text-tiffany-500 hover:underline"
                data-testid="login-link"
              >
                LOGIN
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="pt-20 pb-8 px-3 md:px-8 max-w-7xl mx-auto">
        {/* Back link - smaller */}
        <button 
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-zinc-500 hover:text-tiffany-500 font-mono text-xs mb-4 transition-colors"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="w-3 h-3" />
          BACK
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Product Image - Constrained on desktop */}
          {/* Product Images Gallery */}
          <div className="lg:sticky lg:top-24 lg:self-start space-y-2">
            {/* Main Image with Navigation - Constrained height on desktop */}
            <div 
              className={`relative aspect-[4/3] sm:aspect-[4/5] lg:aspect-[3/4] lg:max-h-[500px] bg-black overflow-hidden border border-zinc-800 cursor-pointer ${isUnavailable ? 'grayscale opacity-70' : ''}`}
              onClick={() => setLightboxOpen(true)}
            >
              <img
                src={getProductImage('medium')}
                alt={product.name}
                className="w-full h-full object-contain bg-zinc-900"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = DEFAULT_PRODUCT_IMAGE;
                }}
              />
              
              {/* Tap to expand hint */}
              <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-0.5 font-mono text-[9px] text-zinc-400 pointer-events-none">
                TAP TO EXPAND
              </div>
              
              {/* Navigation Arrows - only show if multiple images in current view */}
              {displayImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedImageIndex(i => i === 0 ? displayImages.length - 1 : i - 1); }}
                    className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-1.5 transition-colors"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedImageIndex(i => i === displayImages.length - 1 ? 0 : i + 1); }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-1.5 transition-colors"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  
                  {/* Image Counter */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 px-2 py-0.5 font-mono text-[10px] text-white">
                    {selectedImageIndex + 1} / {displayImages.length}
                  </div>
                </>
              )}
            </div>
            
            {/* Thumbnail Strip - only show if multiple images in current view */}
            {displayImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {displayImages.map((imgData, index) => (
                  <button
                    key={imgData.originalIndex}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 border-2 transition-all relative bg-zinc-900 ${
                      selectedImageIndex === index 
                        ? 'border-tiffany-500' 
                        : 'border-zinc-800 hover:border-zinc-600'
                    }`}
                  >
                    <img
                      src={sizedImageUrl(imgData.url, 'thumb')}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                      onLoad={(e) => {
                        e.target.style.opacity = '1';
                      }}
                      onError={(e) => {
                        console.warn('Thumbnail failed to load:', imgData.url);
                        e.target.onerror = null;
                        e.target.src = DEFAULT_PRODUCT_IMAGE;
                      }}
                      style={{ opacity: 0, transition: 'opacity 0.2s' }}
                    />
                    {/* Image number indicator */}
                    <span className="absolute bottom-0 right-0 bg-black/70 text-[8px] text-zinc-400 px-1">
                      #{index + 1}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info - Compact */}
          <div className="space-y-3 lg:space-y-4">
            {/* Category & Brand in one row */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-zinc-500 font-mono text-[11px] uppercase">
                {product.category}
              </span>
              {product.brand && product.brand !== 'None' && (
                <>
                  <span className="text-zinc-700">•</span>
                  <span 
                    className="font-mono text-[10px] px-1.5 py-0.5 uppercase"
                    style={{ 
                      backgroundColor: brandColors[product.brand] || '#2DD4BF',
                      color: ['#000000', '#1F2937', '#111827'].includes(brandColors[product.brand]) ? '#fff' : '#000'
                    }}
                    data-testid="product-brand-label"
                  >
                    {product.brand}
                  </span>
                </>
              )}
            </div>

            {/* Title Row with Share Button */}
            <div className="flex items-start justify-between gap-2">
              <h1 className={`font-sans text-xl sm:text-2xl lg:text-3xl tracking-tight leading-tight uppercase ${isUnavailable ? 'text-zinc-500 line-through' : 'text-white'}`} data-testid="product-title">
                {product.name}
              </h1>
              {/* Share Button */}
              <button
                onClick={handleCopyShareLink}
                className={`flex-shrink-0 p-2 border transition-colors ${
                  shareCopied 
                    ? 'border-green-500 bg-green-500/10 text-green-500' 
                    : 'border-zinc-700 hover:border-tiffany-500 text-zinc-400 hover:text-tiffany-500'
                }`}
                title="Copy share link for social media"
                data-testid="share-product-btn"
              >
                {shareCopied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              </button>
            </div>

            {/* Auction Timer - prominent when applicable */}
            {product.is_bidding && !product.auction_processed && product.stock > 0 && (
              <div className="flex items-center gap-3">
                <span className="bg-tiffany-500 text-black font-bold uppercase tracking-wider text-[10px] px-2 py-1 flex items-center gap-1">
                  <Gavel className="w-3 h-3" />
                  AUCTION
                </span>
                {product.bid_end_time && timeRemaining && timeRemaining !== 'ENDED' && (
                  <span className="bg-zinc-800 text-zinc-300 font-mono text-xs px-2 py-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {timeRemaining} • {formatAuctionEndTime(product.bid_end_time)}
                  </span>
                )}
              </div>
            )}

            {/* Stock warning for non-auction, non-coming-soon */}
            {product.stock <= 3 && product.stock > 0 && !isEndedAuction && !product.is_bidding && product.visibility !== 'coming_soon' && (
              <span className="inline-block bg-red-500/20 text-red-500 font-mono text-[10px] px-2 py-0.5">
                {product.stock} LEFT
              </span>
            )}

            {/* Description - compact */}
            {product.description && (
              <p className="text-zinc-400 font-mono text-xs leading-relaxed">
                {product.description}
              </p>
            )}

            {/* Pricing Section - Compact */}
            <div ref={actionRef} className="border-t border-b border-zinc-800 py-4 space-y-3">
              {product.visibility === 'coming_soon' ? (
                /* Coming Soon - Show badge, hide price and cart */
                <div className="text-center py-6 space-y-4">
                  <div className="inline-flex items-center gap-2 bg-yellow-500 text-black px-4 py-2 font-mono text-lg font-bold">
                    <Clock className="w-5 h-5" />
                    COMING SOON
                  </div>
                  <div className="font-mono text-sm text-zinc-400 uppercase">
                    <p>THIS ITEM WILL BE AVAILABLE SOON.</p>
                    <p>CHECK BACK LATER!</p>
                  </div>
                </div>
              ) : isEndedAuction ? (
                /* Ended Auction (no_bids, reserve_not_met) - Show status, hide bid */
                <div className="text-center py-4">
                  <span className="font-mono text-2xl text-zinc-500 uppercase tracking-wider">
                    {getAuctionEndStatus()}
                  </span>
                </div>
              ) : isSoldAuction ? (
                /* Sold Auction - Show SOLD OUT status */
                <div className="text-center py-4">
                  <span className="font-mono text-2xl text-zinc-500 uppercase tracking-wider">
                    {getAuctionEndStatus() || 'SOLD OUT'}
                  </span>
                </div>
              ) : product.stock === 0 ? (
                /* Regular Sold Out - Show notify button for non-auction items */
                <div className="text-center py-4 space-y-4">
                  <span className="font-mono text-2xl text-zinc-500 uppercase tracking-wider">
                    SOLD OUT
                  </span>
                  {/* One-time item indicator */}
                  {product.is_one_time && (
                    <p className="font-mono text-xs text-zinc-600">ONE TIME ITEM - WILL NOT BE RESTOCKED</p>
                  )}
                  {/* Restock Notification for non-auction, non-one-time items */}
                  {!product.is_bidding && !product.is_one_time && user && (
                    <div className="pt-4">
                      {restockSubscribed ? (
                        <Button
                          onClick={handleRestockUnsubscribe}
                          disabled={subscribingRestock}
                          className="w-full h-12 bg-tiffany-500/20 border border-tiffany-500/50 text-tiffany-500 hover:bg-tiffany-500/30 font-mono text-sm rounded-none flex items-center justify-center gap-2"
                          data-testid="restock-subscribed-btn"
                        >
                          <Bell className="w-4 h-4" />
                          {subscribingRestock ? 'UPDATING...' : 'NOTIFICATIONS ON'}
                        </Button>
                      ) : (
                        <Button
                          onClick={handleRestockSubscribe}
                          disabled={subscribingRestock}
                          className="w-full h-12 bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-tiffany-500 hover:text-tiffany-500 font-mono text-sm rounded-none flex items-center justify-center gap-2 transition-all"
                          data-testid="notify-restock-btn"
                        >
                          <BellOff className="w-4 h-4" />
                          {subscribingRestock ? 'SUBSCRIBING...' : 'NOTIFY WHEN BACK IN STOCK'}
                        </Button>
                      )}
                      <p className="text-zinc-600 font-mono text-xs mt-2">
                        We'll email you when this item is restocked
                      </p>
                    </div>
                  )}
                </div>
              ) : product.is_bidding ? (
                <>
                  {/* OUTBID ALERT - Prominent banner when user has been outbid */}
                  {wasOutbid && !isHighestBidder && (
                    <div className="bg-red-500 text-white p-4 mb-4 animate-pulse">
                      <div className="flex items-center justify-center gap-2">
                        <AlertCircle className="w-6 h-6" />
                        <span className="font-mono text-lg font-bold">YOU'VE BEEN OUTBID!</span>
                      </div>
                      <p className="font-mono text-sm text-center mt-1">
                        Current bid is now ${outbidAmount?.toFixed(2)}
                      </p>
                      <button 
                        onClick={() => setWasOutbid(false)}
                        className="w-full mt-2 py-1 bg-white/20 hover:bg-white/30 font-mono text-xs"
                      >
                        DISMISS
                      </button>
                    </div>
                  )}
                  
                  {/* Current/Starting Bid */}
                  <div className="flex items-baseline justify-between">
                    <span className="text-zinc-500 font-mono text-sm flex items-center gap-2">
                      {product.current_bid ? 'CURRENT BID' : 'STARTING BID'}
                      <span className="flex items-center gap-1 text-[10px] text-green-500">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                        LIVE
                      </span>
                    </span>
                    <span className="font-mono text-3xl text-tiffany-500" data-testid="current-bid">
                      ${(product.current_bid || product.price).toFixed(2)}
                    </span>
                  </div>
                  
                  {/* Bid Count */}
                  <div className="flex items-baseline justify-between">
                    <span className="text-zinc-500 font-mono text-sm">TOTAL BIDS</span>
                    <span className="font-mono text-zinc-300">{product.bid_count}</span>
                  </div>

                  {/* Reserve Status */}
                  {product.reserve_price && (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-zinc-500" />
                      <span className={`font-mono text-sm ${reserveMet ? 'text-tiffany-500' : 'text-zinc-500'}`}>
                        {reserveMet ? 'RESERVE MET' : 'RESERVE NOT MET'}
                      </span>
                    </div>
                  )}

                  {/* Bid Input - only for active auctions */}
                  {timeRemaining !== 'ENDED' && product.stock > 0 && !product.auction_processed && (
                    <div className="pt-4 space-y-3">
                      {isHighestBidder ? (
                        <div className="bg-tiffany-500/10 border border-tiffany-500/30 p-4">
                          <p className="text-tiffany-500 font-mono text-sm text-center">
                            YOU ARE THE HIGHEST BIDDER
                          </p>
                        </div>
                      ) : mustUseBuyNow ? (
                        <div className="bg-yellow-500/10 border border-yellow-500/30 p-4">
                          <p className="text-yellow-500 font-mono text-sm text-center">
                            GAP TO BUY IT NOW IS LESS THAN $5 - USE BUY IT NOW BELOW
                          </p>
                        </div>
                      ) : (
                        <>
                          <label className="text-zinc-500 font-mono text-sm">
                            YOUR BID (MIN: ${minBid.toFixed(2)} - $5 INCREMENT)
                          </label>
                          <div className="flex gap-3">
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono">$</span>
                              <Input
                                type="number"
                                step="5"
                                min={minBid}
                                value={bidAmount}
                                onChange={(e) => {
                                  setBidAmount(e.target.value);
                                  setBidError(''); // Clear error when user types
                                }}
                                className="w-full h-12 pl-8 bg-zinc-900 border-zinc-800 text-white font-mono text-lg focus:border-tiffany-500 rounded-none"
                                data-testid="bid-input"
                              />
                            </div>
                            <Button
                              onClick={handlePlaceBid}
                              disabled={bidding || parseFloat(bidAmount) < minBid}
                              className="h-12 px-8 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none disabled:opacity-50"
                              data-testid="place-bid-btn"
                            >
                              {bidding ? 'PLACING...' : 'PLACE BID'}
                            </Button>
                          </div>
                          {/* Only show warning if bid amount is at or above BIN price */}
                          {maxBid && parseFloat(bidAmount) >= product.buy_now_price && (
                            <p className="text-xs text-yellow-500 font-mono mt-2">
                              Your bid is at or above Buy It Now price. Use Buy It Now instead.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Show bid error if any */}
                  {bidError && (
                    <div className="bg-red-500/10 border border-red-500/30 p-4 mt-3">
                      <p className="text-red-500 font-mono text-sm text-center flex items-center justify-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {bidError}
                      </p>
                    </div>
                  )}

                  {/* Buy Now Option - only for active auctions */}
                  {product.is_bidding && product.buy_now_price && timeRemaining !== 'ENDED' && product.stock > 0 && !product.auction_processed && (
                    <div className="pt-4 border-t border-zinc-800">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-zinc-500 font-mono text-sm flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          BUY IT NOW
                        </span>
                        <span className="font-mono text-xl text-white">
                          ${product.buy_now_price.toFixed(2)}
                        </span>
                      </div>
                      <Button
                        onClick={handleBuyNow}
                        className="w-full h-12 bg-transparent border-2 border-tiffany-500 text-tiffany-500 hover:bg-tiffany-500 hover:text-black font-sans tracking-widest rounded-none transition-all"
                        data-testid="buy-now-btn"
                      >
                        BUY NOW
                      </Button>
                    </div>
                  )}

                  {/* Auto-Bid (Max Bid) Section - only for active auctions */}
                  {product.is_bidding && timeRemaining !== 'ENDED' && product.stock > 0 && !product.auction_processed && (
                    <div className="pt-4 border-t border-zinc-800">
                      {showMaxBidInput ? (
                        // Show max bid input form (for new or updating)
                        <div className="space-y-3">
                          <label className="text-zinc-500 font-mono text-sm flex items-center gap-2">
                            <Zap className="w-4 h-4" />
                            {myMaxBid ? 'UPDATE YOUR MAX BID' : 'SET YOUR MAX BID (AUTO-BID)'}
                          </label>
                          <p className="text-zinc-600 font-mono text-xs">
                            We'll bid for you automatically up to this amount.
                          </p>
                          <div className="flex gap-3">
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono">$</span>
                              <Input
                                type="number"
                                step="5"
                                min={minBid + 5}
                                value={maxBidAmount}
                                onChange={(e) => setMaxBidAmount(e.target.value)}
                                className="w-full h-12 pl-8 bg-zinc-900 border-zinc-800 text-white font-mono text-lg focus:border-yellow-500 rounded-none"
                                placeholder={(minBid + 5).toFixed(2)}
                                data-testid="max-bid-input"
                              />
                            </div>
                            <Button
                              onClick={handleSetMaxBid}
                              disabled={settingMaxBid || parseFloat(maxBidAmount) < minBid + 5}
                              className="h-12 px-6 bg-yellow-500 text-black hover:bg-yellow-400 font-sans tracking-widest rounded-none disabled:opacity-50"
                              data-testid="set-max-bid-btn"
                            >
                              {settingMaxBid ? 'SAVING...' : 'SAVE'}
                            </Button>
                            <Button
                              onClick={() => setShowMaxBidInput(false)}
                              className="h-12 px-4 bg-zinc-800 text-zinc-400 hover:bg-zinc-700 rounded-none"
                            >
                              CANCEL
                            </Button>
                          </div>
                          <p className="text-zinc-600 font-mono text-xs">
                            Min: ${(minBid + 5).toFixed(2)} {product.buy_now_price && `• Max: $${(product.buy_now_price - 0.01).toFixed(2)}`}
                          </p>
                        </div>
                      ) : myMaxBid ? (
                        // Show current max bid status
                        <div className="bg-zinc-900 border border-zinc-800 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-zinc-500 font-mono text-xs flex items-center gap-2">
                              <Zap className="w-4 h-4 text-yellow-500" />
                              AUTO-BID ACTIVE
                            </span>
                            <span className="font-mono text-lg text-yellow-500">
                              UP TO ${myMaxBid.max_amount.toFixed(2)}
                            </span>
                          </div>
                          <p className="text-zinc-600 font-mono text-xs mb-3">
                            We'll automatically bid for you up to your max when outbid.
                          </p>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => {
                                setMaxBidAmount(myMaxBid.max_amount.toFixed(2));
                                setShowMaxBidInput(true);
                              }}
                              className="flex-1 h-9 bg-zinc-800 text-zinc-300 hover:bg-zinc-700 font-mono text-xs rounded-none"
                              disabled={settingMaxBid}
                            >
                              UPDATE
                            </Button>
                            <Button
                              onClick={handleCancelMaxBid}
                              className="flex-1 h-9 bg-red-500/20 text-red-500 hover:bg-red-500/30 font-mono text-xs rounded-none"
                              disabled={settingMaxBid}
                            >
                              {settingMaxBid ? 'CANCELLING...' : 'CANCEL'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Show button to enable max bid
                        <Button
                          onClick={() => setShowMaxBidInput(true)}
                          className="w-full h-10 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-yellow-500/50 hover:text-yellow-500 font-mono text-xs rounded-none transition-all flex items-center justify-center gap-2"
                          data-testid="enable-auto-bid-btn"
                        >
                          <Zap className="w-4 h-4" />
                          SET AUTO-BID (MAX BID)
                        </Button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Regular Price - Compact */}
                  {/* For gift cards, show the selected amount instead of base price */}
                  <div className="flex items-baseline justify-between">
                    <span className="text-zinc-500 font-mono text-xs">PRICE</span>
                    <div className="text-right">
                      <span className="font-mono text-2xl text-white" data-testid="product-price">
                        ${product.is_gift_card 
                          ? (giftCardAmount === 'custom' 
                            ? (parseFloat(giftCardCustomAmount) || 0).toFixed(2)
                            : parseFloat(giftCardAmount).toFixed(2))
                          : (selectedVariant?.price != null ? selectedVariant.price : product.price).toFixed(2)}
                      </span>
                      {/* Show original product price struck through if variant has different price */}
                      {selectedVariant?.price != null && selectedVariant.price !== product.price && (
                        <span className="font-mono text-sm text-zinc-500 line-through ml-2">
                          ${product.price.toFixed(2)}
                        </span>
                      )}
                      {/* BLKCRD Member Price */}
                      {userProfile?.has_blkcrd && !product.is_gift_card && !product.is_blkcrd && !product.is_bidding && (
                        <div className="mt-2 text-right">
                          <span className="font-mono text-sm text-purple-400" data-testid="blkcrd-price">
                            BLKCRD ${((selectedVariant?.price != null ? selectedVariant.price : product.price) * 0.85).toFixed(2)}
                          </span>
                          <span className="font-mono text-xs text-green-500 ml-2">
                            -${((selectedVariant?.price != null ? selectedVariant.price : product.price) * 0.15).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {/* Show BLKCRD savings for non-members as teaser */}
                      {!userProfile?.has_blkcrd && user && !product.is_gift_card && !product.is_blkcrd && !product.is_bidding && (selectedVariant?.price != null ? selectedVariant.price : product.price) >= 20 && (
                        <div className="mt-2 text-right">
                          <span className="font-mono text-[10px] text-zinc-500">
                            BLKCRD ${((selectedVariant?.price != null ? selectedVariant.price : product.price) * 0.85).toFixed(2)} (-${((selectedVariant?.price != null ? selectedVariant.price : product.price) * 0.15).toFixed(2)})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Variant Selector - Tiffany blue boxes */}
                  {/* Gift Card Amount Selector */}
                  {product.is_gift_card ? (
                    <div className="pt-4 space-y-4">
                      <span className="text-zinc-500 font-mono text-sm uppercase">SELECT AMOUNT</span>
                      <div className="flex flex-wrap gap-2">
                        {['25', '50', '100', 'custom'].map((amt) => {
                          const isSelected = giftCardAmount === amt;
                          return (
                            <button
                              key={amt}
                              onClick={() => setGiftCardAmount(amt)}
                              className={`
                                min-w-[70px] px-4 py-3 font-mono text-sm border-2 transition-all
                                ${isSelected 
                                  ? 'bg-tiffany-500 border-tiffany-500 text-black' 
                                  : 'bg-zinc-900 border-zinc-700 text-white hover:border-tiffany-500'
                                }
                              `}
                              data-testid={`gift-card-amount-${amt}`}
                            >
                              {amt === 'custom' ? 'CUSTOM' : `$${amt}`}
                            </button>
                          );
                        })}
                      </div>
                      {giftCardAmount === 'custom' && (
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono">$</span>
                          <Input
                            type="number"
                            value={giftCardCustomAmount}
                            onChange={(e) => setGiftCardCustomAmount(e.target.value)}
                            placeholder="ENTER AMOUNT (1-500)"
                            min="1"
                            max="500"
                            className="w-full h-11 pl-8 bg-zinc-900 border-zinc-800 text-white font-mono focus:border-tiffany-500 rounded-none uppercase"
                            data-testid="gift-card-custom-amount"
                          />
                        </div>
                      )}
                      
                      {/* Recipient Info */}
                      <div className="border-t border-zinc-800 pt-4 space-y-3">
                        <span className="text-zinc-500 font-mono text-sm uppercase">RECIPIENT INFO</span>
                        <Input
                          type="email"
                          value={giftCardRecipientEmail}
                          onChange={(e) => setGiftCardRecipientEmail(e.target.value)}
                          placeholder="RECIPIENT EMAIL (OR YOUR OWN)"
                          className="w-full h-11 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                          data-testid="gift-card-recipient-email"
                        />
                        <Input
                          type="text"
                          value={giftCardRecipientName}
                          onChange={(e) => setGiftCardRecipientName(e.target.value)}
                          placeholder="RECIPIENT NAME (OPTIONAL)"
                          className="w-full h-11 bg-zinc-900 border-zinc-800 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                          data-testid="gift-card-recipient-name"
                        />
                        <textarea
                          value={giftCardMessage}
                          onChange={(e) => setGiftCardMessage(e.target.value)}
                          placeholder="PERSONAL MESSAGE (OPTIONAL)"
                          rows={2}
                          className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-white font-mono text-sm placeholder:text-zinc-600 focus:border-tiffany-500 focus:outline-none rounded-none resize-none"
                          data-testid="gift-card-message"
                        />
                        <p className="text-zinc-600 font-mono text-[10px] uppercase">
                          GIFT CARD CODE WILL BE EMAILED AFTER PAYMENT IS CONFIRMED
                        </p>
                      </div>
                    </div>
                  ) : product.is_blkcrd ? (
                    <div className="pt-4 space-y-4">
                      <span className="text-zinc-500 font-mono text-sm uppercase">SELECT MEMBERSHIP</span>
                      <div className="space-y-2">
                        {Object.entries(BLKCRD_TIERS).map(([key, tier]) => {
                          const isSelected = blkcrdTier === key;
                          return (
                            <button
                              key={key}
                              onClick={() => setBlkcrdTier(key)}
                              className={`
                                w-full px-4 py-4 font-mono text-sm border-2 transition-all text-left
                                ${isSelected 
                                  ? 'bg-tiffany-500 border-tiffany-500 text-black' 
                                  : 'bg-zinc-900 border-zinc-700 text-white hover:border-tiffany-500'
                                }
                              `}
                              data-testid={`blkcrd-tier-${key}`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-bold">{tier.name}</span>
                                <span className="text-lg">${tier.price}</span>
                              </div>
                              {tier.savings > 0 && (
                                <p className={`text-xs mt-1 ${isSelected ? 'text-black/70' : 'text-tiffany-500'}`}>
                                  SAVE ${tier.savings}
                                </p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Auto-renew toggle */}
                      <div className="border-t border-zinc-800 pt-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={blkcrdAutoRenew}
                            onChange={(e) => setBlkcrdAutoRenew(e.target.checked)}
                            className="w-5 h-5 bg-zinc-900 border-zinc-700 rounded-none accent-tiffany-500"
                            data-testid="blkcrd-auto-renew"
                          />
                          <div>
                            <span className="text-white font-mono text-sm">AUTO-RENEW</span>
                            <p className="text-zinc-500 font-mono text-[10px]">
                              AUTOMATICALLY RENEW WHEN MEMBERSHIP EXPIRES
                            </p>
                          </div>
                        </label>
                      </div>
                      
                      {/* Benefits list */}
                      <div className="border-t border-zinc-800 pt-4">
                        <span className="text-zinc-500 font-mono text-xs uppercase">MEMBER BENEFITS</span>
                        <ul className="mt-2 space-y-2">
                          <li className="flex items-center gap-2 text-white font-mono text-sm">
                            <span className="text-tiffany-500">✓</span>
                            15% OFF ALL PURCHASES
                          </li>
                          <li className="flex items-center gap-2 text-white font-mono text-sm">
                            <span className="text-tiffany-500">✓</span>
                            EXCLUSIVE COMING SOON ACCESS
                          </li>
                        </ul>
                      </div>
                    </div>
                  ) : product.has_variants && product.variants?.length > 0 && (
                    <div className="pt-4 space-y-3">
                      <span className="text-zinc-500 font-mono text-sm uppercase">
                        SELECT {product.variant_type === 'size' ? 'SIZE' : product.variant_type === 'number' ? 'NUMBER' : 'OPTION'}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {product.variants.map((variant) => {
                          const isSelected = selectedVariant?.id === variant.id;
                          const isOutOfStock = variant.stock <= 0;
                          
                          return (
                            <button
                              key={variant.id}
                              onClick={() => {
                                if (!isOutOfStock) {
                                  // Toggle: click same variant to deselect
                                  if (isSelected) {
                                    setSelectedVariant(null);
                                  } else {
                                    setSelectedVariant(variant);
                                    setQuantity(1); // Reset quantity when changing variant
                                  }
                                  // Reset to first image
                                  setSelectedImageIndex(0);
                                }
                              }}
                              disabled={isOutOfStock}
                              className={`
                                min-w-[60px] px-4 py-3 font-mono text-sm border-2 transition-all
                                ${isSelected 
                                  ? 'bg-tiffany-500 border-tiffany-500 text-black' 
                                  : isOutOfStock
                                    ? 'bg-zinc-800 border-zinc-700 text-zinc-600 cursor-not-allowed'
                                    : 'bg-zinc-900 border-zinc-700 text-white hover:border-tiffany-500'
                                }
                              `}
                              data-testid={`variant-${variant.id}`}
                            >
                              {variant.name}
                              {variant.price != null && variant.price !== product.price && (
                                <span className="block text-[10px] mt-0.5 opacity-80">${variant.price.toFixed(0)}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {selectedVariant && (
                        <p className="text-xs text-zinc-500 font-mono">
                          {selectedVariant.stock} available
                        </p>
                      )}
                    </div>
                  )}

                  {/* Quantity & Add to Cart */}
                  <div className="pt-3 space-y-3">
                    {/* Only show quantity selector when stock > 1 and for non-variant products or when variant is selected */}
                    {/* Hide quantity for gift cards - they're always qty 1 */}
                    {!product.is_gift_card && (!product.has_variants || selectedVariant) && (product.has_variants ? (selectedVariant?.stock || 0) : product.stock) > 1 && (
                      <div className="flex items-center gap-3">
                        <span className="text-zinc-500 font-mono text-xs">QTY</span>
                        <div className="flex items-center border border-zinc-800">
                          <button
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            className="p-2 text-zinc-400 hover:text-white transition-colors"
                            data-testid="qty-decrease"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-10 text-center font-mono text-white text-sm" data-testid="qty-display">
                            {quantity}
                          </span>
                          <button
                            onClick={() => setQuantity(Math.min(
                              product.has_variants ? (selectedVariant?.stock || 1) : product.stock, 
                              quantity + 1
                            ))}
                            className="p-2 text-zinc-400 hover:text-white transition-colors"
                            data-testid="qty-increase"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Inline notification - appears above buttons */}
                    {inlineNotification && (
                      <div 
                        className={`mb-2 px-3 py-2 font-mono text-xs text-center transition-all duration-300 ${
                          inlineNotification.type === 'success' 
                            ? 'bg-green-500 text-black' 
                            : 'bg-red-500 text-white'
                        }`}
                        data-testid="inline-notification"
                      >
                        {inlineNotification.message}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={handleAddToCart}
                        disabled={!product.is_gift_card && product.has_variants && product.variants?.length > 0 && !selectedVariant}
                        className="h-12 bg-zinc-800 text-white hover:bg-zinc-700 font-mono text-xs tracking-wide rounded-none border border-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="add-to-cart-btn"
                      >
                        ADD TO CART
                      </Button>
                      <Button
                        onClick={handleProceedToCheckout}
                        disabled={!product.is_gift_card && product.has_variants && product.variants?.length > 0 && !selectedVariant}
                        className="h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono text-xs tracking-wide rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="proceed-checkout-btn"
                      >
                        CHECKOUT
                      </Button>
                    </div>
                    
                    {/* Make Offer Button - Only for products with allow_offers enabled */}
                    {/* Hide if user already has a completed offer (accepted, declined, or declined_final) */}
                    {product.allow_offers && !product.is_bidding && product.stock > 0 && 
                     (!userOffer || !['accepted', 'declined', 'declined_final'].includes(userOffer.status)) && (
                      <Button
                        onClick={() => {
                          if (product.has_variants && product.variants?.length > 0 && !selectedVariant) {
                            setVariantPicker({ open: true, next: 'offer' });
                            return;
                          }
                          setShowOfferModal(true);
                        }}
                        variant="outline"
                        className="w-full h-10 mt-2 rounded-none border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 font-mono text-xs"
                        data-testid="make-offer-btn"
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        MAKE AN OFFER
                      </Button>
                    )}
                    
                    {/* Trade Offer Button - For regular products, not gift cards, BLKCRD, auctions, or international customers */}
                    {!product.is_gift_card && 
                     !product.is_blkcrd && 
                     !product.is_bidding && 
                     product.stock > 0 && 
                     product.category?.toLowerCase() !== 'gift cards' &&
                     product.category?.toLowerCase() !== 'blkcrd' &&
                     !product.name?.toLowerCase().includes('blkcrd') &&
                     !product.name?.toLowerCase().includes('gift card') &&
                     !userProfile?.is_international && (
                      <Button
                        onClick={() => {
                          if (!user) {
                            toast.error('PLEASE LOGIN TO OFFER A TRADE');
                            navigate('/login');
                            return;
                          }
                          if (product.has_variants && product.variants?.length > 0 && !selectedVariant) {
                            setVariantPicker({ open: true, next: 'trade' });
                            return;
                          }
                          setShowTradeOfferModal(true);
                        }}
                        variant="outline"
                        className="w-full h-10 mt-2 rounded-none border-purple-500/50 text-purple-400 hover:bg-purple-500/10 font-mono text-xs"
                        data-testid="offer-trade-btn"
                      >
                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                        OFFER TRADE
                      </Button>
                    )}
                    
                    {/* Payment Plan Options - Show if any plan is enabled */}
                    {(product.flex_pay_enabled || product.standard_plan_enabled) && !product.is_bidding && product.stock > 0 && (
                      <div className="mt-3 p-3 border border-tiffany-500/30 bg-tiffany-500/10">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4 text-tiffany-500" />
                          <span className="font-mono text-xs text-tiffany-500">PAYMENT PLANS AVAILABLE</span>
                        </div>
                        
                        {/* Standard Plan Option (2 payments) */}
                        {product.standard_plan_enabled && (
                          <div className="mb-2 p-2 bg-zinc-900/50 border border-zinc-800">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-mono text-xs text-white font-bold">STANDARD PLAN</span>
                              <span className="font-mono text-[10px] text-orange-400">+20% FEE</span>
                            </div>
                            <p className="font-mono text-[10px] text-zinc-400 mb-1">
                              50% DOWN (${((selectedVariant?.price != null ? selectedVariant.price : product.price) * 1.20 * 0.50).toFixed(2)}) • 2 PAYMENTS • 7 DAYS
                            </p>
                            <p className="font-mono text-[10px] text-zinc-500">
                              TOTAL: ${((selectedVariant?.price != null ? selectedVariant.price : product.price) * 1.20).toFixed(2)}
                            </p>
                            {user ? (
                              <Button
                                size="sm"
                                onClick={() => handleStartPaymentPlan('standard')}
                                disabled={startingPlan}
                                className="w-full mt-2 h-7 rounded-none bg-tiffany-500 hover:bg-tiffany-400 text-black font-mono text-xs"
                                data-testid="start-standard-plan-btn"
                              >
                                {startingPlan === 'standard' ? 'STARTING...' : 'START STANDARD'}
                              </Button>
                            ) : (
                              <p className="font-mono text-[10px] text-yellow-600 mt-2">LOGIN TO START A PAYMENT PLAN</p>
                            )}
                          </div>
                        )}
                        
                        {/* Flex Pay Option (3 payments) - Only for items $350+ */}
                        {product.flex_pay_enabled && (selectedVariant?.price != null ? selectedVariant.price : product.price) >= 350 && (
                          <div className="p-2 bg-zinc-900/50 border border-zinc-800">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-mono text-xs text-white font-bold">FLEX PAY</span>
                              <span className="font-mono text-[10px] text-orange-400">+20% FEE</span>
                            </div>
                            <p className="font-mono text-[10px] text-zinc-400 mb-1">
                              33% DOWN (${((selectedVariant?.price != null ? selectedVariant.price : product.price) * 1.20 / 3).toFixed(2)}) • 3 PAYMENTS • 14 DAYS
                            </p>
                            <p className="font-mono text-[10px] text-zinc-500">
                              TOTAL: ${((selectedVariant?.price != null ? selectedVariant.price : product.price) * 1.20).toFixed(2)}
                            </p>
                            {user ? (
                              <Button
                                size="sm"
                                onClick={() => handleStartPaymentPlan('flex_pay')}
                                disabled={startingPlan}
                                className="w-full mt-2 h-7 rounded-none bg-orange-600 hover:bg-orange-500 text-white font-mono text-xs"
                                data-testid="start-flex-pay-btn"
                              >
                                {startingPlan === 'flex_pay' ? 'STARTING...' : 'START FLEX PAY'}
                              </Button>
                            ) : (
                              <p className="font-mono text-[10px] text-yellow-600 mt-2">LOGIN TO START A PAYMENT PLAN</p>
                            )}
                          </div>
                        )}
                        
                        {/* Flex Pay not available message for items under $350 */}
                        {product.flex_pay_enabled && (selectedVariant?.price != null ? selectedVariant.price : product.price) < 350 && !product.standard_plan_enabled && (
                          <p className="font-mono text-[10px] text-yellow-600">
                            Standard plan only available for items $350+
                          </p>
                        )}
                        
                        <p className="font-mono text-[10px] text-red-400 mt-2">
                          ⚠️ Down payments are NON-REFUNDABLE
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Bid History - Only show for active auctions or sold auctions, hide for reserve_not_met and no_bids */}
            {product.is_bidding && bids.length > 0 && !isEndedAuction && (
              <div className="space-y-4">
                <h3 className="font-sans text-xl text-white">
                  BID HISTORY {user?.is_admin && <span className="text-tiffany-500 text-sm">(ADMIN VIEW)</span>}
                </h3>
                <div className="border border-zinc-800 divide-y divide-zinc-800 max-h-64 overflow-y-auto">
                  {bids.map((bid, index) => (
                    <div 
                      key={bid.id} 
                      className={`p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${index === 0 ? 'bg-tiffany-500/10' : ''}`}
                    >
                      <div className="flex-1 min-w-0">
                        {/* Only admins see bidder names */}
                        {user?.is_admin ? (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <span className="font-mono text-white text-sm truncate">
                              {bid.user_full_name || bid.bidder_name || 'Anonymous'}
                            </span>
                            {bid.user_email && (
                              <span className="font-mono text-tiffany-500 text-xs truncate">
                                {bid.user_email}
                              </span>
                            )}
                            <span className="font-mono text-zinc-500 text-xs">
                              {new Date(bid.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-zinc-500">
                              Bid #{bids.length - index}
                            </span>
                            <span className="font-mono text-zinc-500 text-xs">
                              {new Date(bid.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                        )}
                      </div>
                      <span className={`font-mono text-lg shrink-0 ${index === 0 ? 'text-tiffany-500' : 'text-zinc-400'}`}>
                        ${bid.amount.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* Related Products Section */}
      {relatedProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-8 border-t border-zinc-800">
          <h2 className="font-mono text-lg text-zinc-400 mb-6">YOU MIGHT ALSO LIKE</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {relatedProducts.map((relatedProduct) => {
              // Get the image URL, with multiple fallbacks
              const imageUrl = relatedProduct.images?.[0] || relatedProduct.image;
              const resolvedImage = imageUrl ? sizedImageUrl(imageUrl, 'thumb') : DEFAULT_PRODUCT_IMAGE;
              
              return (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="group bg-zinc-900 border border-zinc-800 hover:border-tiffany-500/50 transition-all duration-300"
                  data-testid={`related-product-${relatedProduct.id}`}
                >
                  {/* Image */}
                  <div className="aspect-square bg-zinc-800 overflow-hidden">
                    <img
                      src={resolvedImage}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => {
                        if (e.target.src !== DEFAULT_PRODUCT_IMAGE) {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_PRODUCT_IMAGE;
                        }
                      }}
                    />
                  </div>
                  
                  {/* Info */}
                  <div className="p-3">
                    {/* Brand - Always uppercase */}
                    {relatedProduct.brand && (
                      <p 
                        className="font-mono text-[10px] mb-1 truncate uppercase"
                        style={{ color: brandColors[relatedProduct.brand?.toUpperCase()] || brandColors[relatedProduct.brand] || '#71717a' }}
                      >
                        {relatedProduct.brand.toUpperCase()}
                      </p>
                    )}
                    
                    {/* Name */}
                    <p className="font-mono text-sm text-white truncate group-hover:text-tiffany-500 transition-colors">
                      {relatedProduct.name}
                    </p>
                    
                    {/* Price */}
                    <p className="font-mono text-tiffany-500 mt-1">
                      ${relatedProduct.price?.toFixed(2)}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
      
      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 text-white hover:text-tiffany-500 transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>
          
          {/* Image */}
          <div className="relative w-full h-full flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={getProductImage()}
              alt={product?.name}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = DEFAULT_PRODUCT_IMAGE;
              }}
            />
            
            {/* Navigation Arrows in Lightbox */}
            {product?.images && product.images.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedImageIndex(i => i === 0 ? product.images.length - 1 : i - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 transition-colors"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={() => setSelectedImageIndex(i => i === product.images.length - 1 ? 0 : i + 1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 transition-colors"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 px-4 py-2 font-mono text-sm text-white">
                  {selectedImageIndex + 1} / {product.images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Make Offer Modal */}
      <MakeOfferModal
        isOpen={showOfferModal}
        onClose={() => setShowOfferModal(false)}
        product={product}
        selectedVariant={selectedVariant}
        token={localStorage.getItem('blkmrkt_token')}
        existingOffer={userOffer}
        onOfferSubmitted={() => {
          // Refresh user's offer status
          axios.get(`${API}/products/${id}/my-offer`, {
            headers: getAuthHeader()
          }).then(res => setUserOffer(res.data)).catch(() => {});
        }}
      />
      
      {/* Trade Offer Modal */}
      <TradeOfferModal
        isOpen={showTradeOfferModal}
        onClose={() => setShowTradeOfferModal(false)}
        product={product}
        selectedVariant={selectedVariant}
      />
      
      {/* Variant Picker — shown when user clicks an offer button without picking a variant */}
      {variantPicker.open && product?.variants?.length > 0 && (
        <div
          className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setVariantPicker({ open: false, next: null })}
          data-testid="variant-picker-modal"
        >
          <div
            className="bg-[#0a0a0a] border border-tiffany-500/40 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-zinc-800 px-4 py-3">
              <h2 className="font-mono text-sm text-white tracking-widest">SELECT A VARIANT</h2>
              <p className="font-mono text-[11px] text-zinc-500 mt-1">
                {variantPicker.next === 'trade' ? 'Which variant do you want to trade for?' : 'Which variant do you want to make an offer on?'}
              </p>
            </div>
            <div className="p-3 space-y-1 max-h-80 overflow-y-auto">
              {product.variants.map((v) => {
                const outOfStock = (v.stock || 0) < 1;
                return (
                  <button
                    key={v.id}
                    disabled={outOfStock}
                    onClick={() => {
                      setSelectedVariant(v);
                      const next = variantPicker.next;
                      setVariantPicker({ open: false, next: null });
                      if (next === 'trade') {
                        setShowTradeOfferModal(true);
                      } else if (next === 'offer') {
                        setShowOfferModal(true);
                      }
                    }}
                    className={`w-full flex items-center justify-between px-3 py-3 border border-zinc-800 hover:border-tiffany-500 hover:bg-tiffany-500/5 font-mono text-left transition-colors ${outOfStock ? 'opacity-40 cursor-not-allowed hover:border-zinc-800 hover:bg-transparent' : ''}`}
                    data-testid={`variant-picker-option-${v.id}`}
                  >
                    <span className="text-white text-sm">{v.name}</span>
                    <span className={`text-[10px] tracking-widest ${outOfStock ? 'text-red-500' : 'text-zinc-500'}`}>
                      {outOfStock ? 'SOLD OUT' : `STOCK: ${v.stock}`}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="border-t border-zinc-800 p-3">
              <button
                onClick={() => setVariantPicker({ open: false, next: null })}
                className="w-full text-zinc-400 hover:text-white font-mono text-xs py-2 tracking-widest"
                data-testid="variant-picker-cancel"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Payment Plan Confirmation Dialog */}
      <AlertDialog open={paymentPlanDialog.open} onOpenChange={(open) => !open && setPaymentPlanDialog({ open: false, planType: null })}>
        <AlertDialogContent className="bg-zinc-900 border-2 border-tiffany-500/50 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-mono text-xl text-white tracking-widest text-center pb-2 border-b border-zinc-800">
              START {paymentPlanDialog.planType === 'flex_pay' ? 'FLEX PAY' : 'STANDARD'} PLAN?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              {paymentPlanDialog.planType && product && (() => {
                const details = getPaymentPlanDetails(
                  paymentPlanDialog.planType,
                  paymentPlanDialog.priorityShipping && !paymentPlanDialog.overnightShipping,
                  paymentPlanDialog.overnightShipping,
                );
                if (!details) return null;
                return (
                  <div className="space-y-4 mt-4">
                    {/* Priority Shipping Toggle */}
                    {!fbconMode && (
                    <div 
                      onClick={() => setPaymentPlanDialog(prev => ({
                        ...prev,
                        priorityShipping: !prev.priorityShipping,
                        // Mutually exclusive with overnight
                        overnightShipping: !prev.priorityShipping ? false : prev.overnightShipping,
                      }))}
                      className={`flex items-center justify-between p-3 border cursor-pointer transition-all ${
                        paymentPlanDialog.priorityShipping 
                          ? 'border-tiffany-500 bg-tiffany-500/10' 
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Zap className={`w-4 h-4 ${paymentPlanDialog.priorityShipping ? 'text-tiffany-400' : 'text-zinc-500'}`} />
                        <div>
                          <p className="font-mono text-sm text-white tracking-wide">PRIORITY SHIPPING</p>
                          <p className="font-mono text-[10px] text-zinc-500">2-3 DAY DELIVERY</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-tiffany-400">+$15.00</span>
                        <div className={`w-5 h-5 border-2 flex items-center justify-center ${
                          paymentPlanDialog.priorityShipping 
                            ? 'border-tiffany-500 bg-tiffany-500' 
                            : 'border-zinc-600'
                        }`}>
                          {paymentPlanDialog.priorityShipping && <Check className="w-3 h-3 text-black" />}
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Overnight Shipping Toggle — same UX, mutually exclusive */}
                    {!fbconMode && (
                    <div
                      onClick={() => setPaymentPlanDialog(prev => ({
                        ...prev,
                        overnightShipping: !prev.overnightShipping,
                        priorityShipping: !prev.overnightShipping ? false : prev.priorityShipping,
                      }))}
                      className={`flex items-center justify-between p-3 border cursor-pointer transition-all ${
                        paymentPlanDialog.overnightShipping
                          ? 'border-tiffany-500 bg-tiffany-500/10'
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Zap className={`w-4 h-4 ${paymentPlanDialog.overnightShipping ? 'text-tiffany-400' : 'text-zinc-500'}`} />
                        <div>
                          <p className="font-mono text-sm text-white tracking-wide">OVERNIGHT SHIPPING</p>
                          <p className="font-mono text-[10px] text-zinc-500">NEXT-BUSINESS-DAY DELIVERY</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-tiffany-400">+$50.00</span>
                        <div className={`w-5 h-5 border-2 flex items-center justify-center ${
                          paymentPlanDialog.overnightShipping
                            ? 'border-tiffany-500 bg-tiffany-500'
                            : 'border-zinc-600'
                        }`}>
                          {paymentPlanDialog.overnightShipping && <Check className="w-3 h-3 text-black" />}
                        </div>
                      </div>
                    </div>
                    )}
                    
                    {/* Payment Schedule Header */}
                    <p className="font-mono text-[10px] text-zinc-500 tracking-widest">PAYMENT SCHEDULE</p>
                    
                    {/* Payment Schedule */}
                    <div className="space-y-0 border border-zinc-800">
                      {details.schedule.map((payment, idx) => (
                        <div key={idx} className={`flex justify-between items-center p-3 ${idx !== details.schedule.length - 1 ? 'border-b border-zinc-800' : ''}`}>
                          <div>
                            <p className="font-mono text-sm text-white tracking-wide">{payment.label}</p>
                            <p className="font-mono text-[10px] text-zinc-500 tracking-wider mt-0.5">{payment.due}</p>
                          </div>
                          <span className="font-mono text-base text-white font-bold">${payment.amount.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Price Breakdown */}
                    <div className="bg-zinc-800/50 p-4 space-y-2 border border-zinc-700">
                      <p className="font-mono text-[10px] text-zinc-500 tracking-widest mb-3">PRICE BREAKDOWN</p>
                      <div className="flex justify-between font-mono text-xs text-zinc-400">
                        <span className="tracking-wide">BASE PRICE</span>
                        <span>${details.basePrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-mono text-xs text-zinc-400">
                        <span className="tracking-wide">ARRANGEMENT FEE (20%)</span>
                        <span>${details.arrangementFee.toFixed(2)}</span>
                      </div>
                      {details.shippingCost > 0 && (
                        <div className="flex justify-between font-mono text-xs text-tiffany-400">
                          <span className="tracking-wide">
                            {paymentPlanDialog.overnightShipping ? 'OVERNIGHT SHIPPING' : 'PRIORITY SHIPPING'}
                          </span>
                          <span>${details.shippingCost.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-mono text-sm text-white font-bold pt-3 mt-2 border-t border-zinc-600">
                        <span className="tracking-wider">TOTAL</span>
                        <span className="text-tiffany-400">${details.total.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    {/* Warning */}
                    <div className="flex items-center gap-3 p-4 bg-red-900/30 border border-red-500/50">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <p className="font-mono text-xs text-red-400 tracking-wide">DOWN PAYMENT IS NON-REFUNDABLE</p>
                    </div>
                  </div>
                );
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6 flex-col gap-2 sm:flex-col">
            <AlertDialogAction 
              onClick={confirmStartPaymentPlan}
              className="w-full bg-tiffany-500 hover:bg-tiffany-400 text-black font-mono tracking-widest py-3"
            >
              CONFIRM PLAN
            </AlertDialogAction>
            <AlertDialogCancel className="w-full bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 font-mono tracking-wider">
              CANCEL
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
